/* eslint-disable react/forbid-dom-props */
import React, { useCallback, useEffect, useRef } from 'react';
import styles from './MultiRangeSlider.module.scss';
import { MultiRangeSliderProps } from './types';

const clamp = (val: number, min: number, max: number) =>
  Math.min(Math.max(val, min), max);

const MultiRangeSlider: React.FC<MultiRangeSliderProps> = ({
  min,
  max,
  step = 1,
  valueMin,
  valueMax,
  onChange,
  onCommit,
}) => {
  const rangeRef = useRef<HTMLDivElement>(null);

  // Convert to percentage with clamping
  const getPercent = useCallback(
    (value: number) => {
      const percent = ((value - min) / (max - min)) * 100;

      return clamp(Math.round(percent), 0, 100);
    },
    [min, max],
  );

  // Update range bar visuals whenever values change
  useEffect(() => {
    let minPercent = getPercent(valueMin);
    let maxPercent = getPercent(valueMax);

    // Ensure minPercent is not greater than maxPercent
    if (minPercent > maxPercent) {
      const temp = minPercent;

      minPercent = maxPercent;
      maxPercent = temp;
    }

    if (rangeRef.current) {
      rangeRef.current.style.left = `${minPercent}%`;
      rangeRef.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [valueMin, valueMax, getPercent]);

  // Commit helper
  const commit = () => {
    if (onCommit) {
      onCommit({ min: valueMin, max: valueMax });
    }
  };

  return (
    <div className={`${styles.container} mb-4 mt-8`}>
      <input
        type="range"
        step={step}
        min={min}
        max={max}
        value={valueMin}
        onChange={(event) => {
          const newVal = Math.min(Number(event.target.value), valueMax - step);

          onChange({ min: newVal, max: valueMax });
        }}
        onMouseUp={commit}
        onTouchEnd={commit}
        className={`${styles.thumb} ${styles['thumb--left']}`}
        style={{ zIndex: valueMin > max - (max - min) * 0.1 ? 5 : undefined }}
      />

      <input
        type="range"
        step={step}
        min={min}
        max={max}
        value={valueMax}
        onChange={(event) => {
          const newVal = Math.max(Number(event.target.value), valueMin + step);

          onChange({ min: valueMin, max: newVal });
        }}
        onMouseUp={commit}
        onTouchEnd={commit}
        className={`${styles.thumb} ${styles['thumb--right']}`}
      />

      <div className={styles.slider}>
        <div className={styles.slider__track} />
        <div ref={rangeRef} className={styles.slider__range} />
      </div>
    </div>
  );
};

export default MultiRangeSlider;
